import * as React from 'react';
import { Form } from 'reactstrap';
import { SelectedFilter } from '.';
import { getFilterType, FilterProviderBase } from './providers';
import LeftChevronSVG from 'components/shared/svg/LeftChevronSVG';
import PopoverTitle from 'components/shared/PopoverTitle';

interface FilterEditProps {
	onFilterChange: (selectedFilter: SelectedFilter, stopEditing: boolean) => void;
	onFilterRemoved: (selectedFilter: SelectedFilter, stopEditing: boolean) => void;
	cancelFilterChange: () => void;
	closeList: () => void;
	selectedFilter: SelectedFilter;
	handlePopoverClose: () => void;
}

interface FilterEditState {
	value: any;
	displayValidationError: boolean;
}

/**
 * Component that manages how the value of a filter is set and updated.
 */
class FilterEdit extends React.PureComponent<FilterEditProps, FilterEditState> {
	private isEditMode: boolean = false;
	private EditComponent: any;
	private filter: FilterProviderBase<any>;
	private EditComponentProps: any;

	constructor(props: FilterEditProps) {
		super(props);

		const {
			selectedFilter: { value, type },
			selectedFilter,
		} = props;

		this.isEditMode = value !== undefined;

		// dynamically pick the component needed for the current filter type and gets the component needed
		// to handle value adding/editing.
		this.filter = getFilterType(type);

		this.EditComponent = this.filter.getComponent();
		this.EditComponentProps = this.filter.getComponentProps(selectedFilter);

		this.state = {
			value,
			displayValidationError: false,
		};
	}

	/**
	 * Handles changes and stores them in local state to avoid sending state up until it's necessary
	 *
	 * @private
	 * @memberof FilterEdit
	 */
	private handleOnChange = (value: any, stopEditing: boolean = true) => {
		const hasNoValue = this.filter.isEmptyValue(value);

		if (hasNoValue) {
			this.props.onFilterRemoved(this.props.selectedFilter, true);
		}

		this.setState(
			{
				value,
			},
			() => {
				this.handleFilterSave(stopEditing);
			},
		);
	};

	/**
	 * When a filter is done being modified, it will be added/updated here. This will also update state
	 * further up by calling the provided callbacks.
	 *
	 * @private
	 * @memberof FilterEdit
	 */
	private handleFilterSave = (stopEditing: boolean = true) => {
		const { value } = this.state;

		const hasNoValue = this.filter.isEmptyValue(this.state.value);

		if (hasNoValue && !this.props.selectedFilter.nullable) {
			this.props.onFilterRemoved(this.props.selectedFilter, true);
			return;
		}

		this.props.onFilterChange({ ...this.props.selectedFilter, value }, stopEditing);
	};

	public handleCloseClick = () => {
		this.props.cancelFilterChange();
		this.props.closeList();
	};

	public render() {
		const EditComponent = this.EditComponent;

		return (
			<>
				<PopoverTitle
					titleText={this.props.selectedFilter.displayName}
					close={this.props.handlePopoverClose}
					renderBack={() => {
						return (
							<div className="popover-title-back">
								<button
									onClick={() => {
										this.props.cancelFilterChange();
									}}
									className="filters-search__title-chevron-btn"
								>
									<LeftChevronSVG class="filters-search__title-chevron" />
								</button>
							</div>
						);
					}}
				/>

				<Form
					onSubmit={(e) => {
						e.preventDefault();
					}}
				>
					<EditComponent
						value={this.state.value}
						filter={this.props.selectedFilter}
						onChange={this.handleOnChange}
						{...this.EditComponentProps}
					/>
				</Form>
			</>
		);
	}
}

export default FilterEdit;
